// The build column's FRAME pieces (prototype wk-step-head / wk-step-foot / stepBody dispatch): the
// per-step heading, the review foot ("Reviewing — nothing runs yet" + Back / Confirm & continue),
// and the switch that renders one step body from the projection. The foot keeps the house
// one-primary-per-view rule: Confirm & continue is primary on review steps but GHOST on the Brief
// step (Draft the configuration is that view's primary) and absent on the gate step. All content
// derives from the projection (G-182); tokens only (G-181).

import type { WorkComposerProjection, WorkView } from '../../projections.ts';
import { composerStepHeading, type ComposerStep } from '../composer-model.ts';
import { GHOST_BUTTON_CLASS, PRIMARY_BUTTON_CLASS } from '../composer-ui.tsx';
import { ActivateGate } from './ActivateGate.tsx';
import { BriefStep } from './BriefStep.tsx';
import { BindingsStep, LifecycleStep, PaletteStep, SkillStep } from './config-steps.tsx';
import { ApproversStep, ViewCadenceStep } from './view-steps.tsx';

/** The step heading: the compose eyebrow, the step title, and its explanatory sub. */
export function StepHead({
  step,
  workflowName,
}: {
  readonly step: ComposerStep;
  readonly workflowName: string;
}): React.JSX.Element {
  const heading = composerStepHeading(step.id, workflowName);
  return (
    <div className="mb-6">
      <div className="ritona-numeric text-xs uppercase tracking-eyebrow text-text-subtle">
        Compose · step {step.number} of 08
      </div>
      <h1 className="ritona-voice mt-2 text-2xl font-medium tracking-snug text-text">
        {heading.title}
      </h1>
      <p className="mt-1 max-w-prose text-sm leading-relaxed text-text-muted">{heading.sub}</p>
    </div>
  );
}

export interface StepFootProps {
  readonly stepIndex: number;
  /** False on the Brief step, whose primary is Draft the configuration (one primary per view). */
  readonly primaryNext: boolean;
  readonly onBack: () => void;
  readonly onNext: () => void;
}

/** The review foot under every non-gate step. */
export function StepFoot({
  stepIndex,
  primaryNext,
  onBack,
  onNext,
}: StepFootProps): React.JSX.Element {
  return (
    <div className="mt-8 flex items-center justify-between gap-4 border-t border-border pt-5">
      {stepIndex > 0 ? (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back a step"
          className={GHOST_BUTTON_CLASS}
        >
          Back
        </button>
      ) : (
        <span />
      )}
      <div className="flex items-center gap-5">
        <span className="ritona-numeric text-xs uppercase tracking-eyebrow text-text-subtle">
          Reviewing — nothing runs yet
        </span>
        <button
          type="button"
          onClick={onNext}
          aria-label="Confirm and continue"
          className={primaryNext ? PRIMARY_BUTTON_CLASS : GHOST_BUTTON_CLASS}
        >
          Confirm & continue
        </button>
      </div>
    </div>
  );
}

export interface StepBodyProps {
  readonly step: ComposerStep;
  readonly config: WorkComposerProjection;
  readonly activeView: WorkView;
  readonly busy: boolean;
  readonly onDraft: () => void;
  readonly onPickView: (view: WorkView) => void;
  readonly onActivate: () => Promise<void>;
  readonly onSaveDraft: () => void;
}

/** Render the open step's body from the projection. */
export function StepBodySwitch(props: StepBodyProps): React.JSX.Element {
  const { step, config } = props;
  switch (step.id) {
    case 'brief':
      return (
        <BriefStep
          workflowName={config.workflowName}
          ownerAsk={config.ownerAsk}
          onDraft={props.onDraft}
        />
      );
    case 'skill':
      return <SkillStep skill={config.skill} />;
    case 'bindings':
      return <BindingsStep bindings={config.bindings} />;
    case 'lifecycle':
      return <LifecycleStep lifecycle={config.lifecycle} />;
    case 'actions':
      return <PaletteStep palette={config.palette} />;
    case 'view':
      return (
        <ViewCadenceStep
          options={config.viewOptions}
          activeView={props.activeView}
          onPick={props.onPickView}
          cadenceLabel={config.cadenceLabel}
          timezone={config.timezone}
          boardCap={config.boardCap}
        />
      );
    case 'approvers':
      return <ApproversStep approvers={config.approvers} />;
    case 'activate':
      return (
        <ActivateGate
          config={config}
          activeView={props.activeView}
          busy={props.busy}
          onActivate={props.onActivate}
          onSaveDraft={props.onSaveDraft}
        />
      );
  }
}
